/**
 * Universal Addon Manager
 * Manages product addons (like hemming), cart addons (like gift wrapping), 
 * and automatic cart additions (like novelty items).
 */
class UniversalAddonManager {
  constructor() {
    this.settings = window.UniversalAddonSettings || { noveltyEnabled: false, noveltyVariantId: '', noveltyThreshold: 0 };
    this.cartAddonSelector = '[data-universal-addon-trigger="cart_checkbox"]';

    // Bind methods
    this.handleCartChangeEvents = this.handleCartChangeEvents.bind(this);
    this.handleCartCheckboxChange = this.handleCartCheckboxChange.bind(this);
    this.handleProductFormSubmit = this.handleProductFormSubmit.bind(this);

    this.init();
  }

  init() {
    document.documentElement.addEventListener('cart:change', this.handleCartChangeEvents, true);
    document.documentElement.addEventListener('cart:refresh', this.handleCartChangeEvents, true);
    document.addEventListener('change', this.handleCartCheckboxChange, true);
    document.addEventListener('submit', this.handleProductFormSubmit, true);

    if (window.location.pathname.includes('/cart')) {
      this.checkCartRules();
    }

    this.initDeliveryAppBlocker();
  }

  /* =========================================================================
   * DELIVERY APP BLOCKER (NGC)
   * ========================================================================= */
  initDeliveryAppBlocker() {
    const disableCheckoutButtons = (disable) => {
      document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach(btn => {
        if (disable) {
          btn.disabled = true;
          btn.style.opacity = '0.5';
          btn.style.cursor = 'not-allowed';
        } else {
          btn.disabled = false;
          btn.style.opacity = '1';
          btn.style.cursor = 'pointer';
        }
      });
    };

    const checkAppLoaded = () => document.querySelector('._NGC_CFA_shippingdate_select') !== null;

    const startAppObserver = () => {
      if (!document.querySelector('form[action^="/cart"]')) return;

      if (checkAppLoaded()) {
        disableCheckoutButtons(false);
        return;
      }

      disableCheckoutButtons(true);

      const observer = new MutationObserver(() => {
        if (checkAppLoaded()) {
          disableCheckoutButtons(false);
          observer.disconnect();
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => {
        disableCheckoutButtons(false);
        observer.disconnect();
      }, 8000);
    };

    startAppObserver();
    document.documentElement.addEventListener('cart:refresh', () => {
      setTimeout(startAppObserver, 100);
    });
  }

  /* =========================================================================
   * PRODUCT ADDONS (Hemming etc.)
   * ========================================================================= */
  async handleProductFormSubmit(e) {
  }

  /* =========================================================================
   * CART ADDONS (Gift Wrapping)
   * ========================================================================= */
  async handleCartCheckboxChange(e) {
    const target = e.target;
    if (!target.matches(this.cartAddonSelector)) return;

    const isChecked = target.checked;
    const variantIdToAdd = Number(target.getAttribute('data-variant-id'));
    const isSyncQuantity = target.getAttribute('data-sync-quantity') === 'true';

    document.querySelectorAll(this.cartAddonSelector).forEach(cb => {
      if (cb !== target) {
        if (cb.dataset.variantId === target.dataset.variantId) {
          cb.checked = isChecked;
        } else if (isChecked) {
          cb.checked = false;
        }
      }
    });

    try {
      const cartStatus = await (await fetch('/cart.js')).json();
      const giftIds = Array.from(document.querySelectorAll(this.cartAddonSelector)).map(cb => Number(cb.dataset.variantId)).filter(id => id > 0);
      const nonGiftQty = cartStatus.items.filter(i => !giftIds.includes(i.id)).reduce((sum, i) => sum + i.quantity, 0);
      const removes = cartStatus.items.filter(i => giftIds.includes(i.id)).map(i => {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: i.key, quantity: 0 })
        });
      });

      await Promise.all(removes);

      if (isChecked) {
        const qtyToAdd = isSyncQuantity ? nonGiftQty : 1;
        if (qtyToAdd > 0) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: variantIdToAdd,
              quantity: qtyToAdd,
              properties: { _gift_option: 'true' }
            })
          });
        }
      }

      this.triggerCartUpdate();
    } catch (e) {
      console.error("[Universal Addon] Error updating cart addon", e);
      target.checked = !isChecked;
    }
  }

  /* =========================================================================
   * CART AUTO-ADD (Novelty) & CART CHANGE RULES
   * ========================================================================= */
  handleCartChangeEvents(e) {
    if (this._isUpdatingRules) return;
    this.checkCartRules();
  }

  async checkCartRules() {
    this._isUpdatingRules = true;
    try {
      const cart = await (await fetch('/cart.js?_=' + Date.now())).json();
      let updatesNeeded = {};
      let itemsToAdd = [];

      if (this.settings.noveltyEnabled && this.settings.noveltyVariantId) {
        const noveltyId = Number(this.settings.noveltyVariantId);
        const threshold = (Number(this.settings.noveltyThreshold) || 0) * 100;
        const cartTotal = cart.items.reduce((sum, i) => sum + (i.id !== noveltyId ? i.original_line_price : 0), 0);
        const hasOptedOut = sessionStorage.getItem('universal_addon_novelty_opt_out') === 'true';
        const noveltyItem = cart.items.find(i => i.id === noveltyId);
        const isAvailable = this.settings.noveltyVariantAvailable !== false;

        if (!hasOptedOut) {
          if (cartTotal >= threshold && !noveltyItem && isAvailable) {
            itemsToAdd.push({ id: noveltyId, quantity: 1, properties: { _novelty: 'true' } });
          } else if ((cartTotal < threshold || !isAvailable) && noveltyItem) {
            updatesNeeded[noveltyItem.key] = 0;
          }
        } else if (noveltyItem) {
          updatesNeeded[noveltyItem.key] = 0;
        }
      }

      const giftBoxes = Array.from(document.querySelectorAll(this.cartAddonSelector));
      const activeGiftBox = giftBoxes.find(b => b.checked);

      if (activeGiftBox) {
        const isSyncQuantity = activeGiftBox.getAttribute('data-sync-quantity') === 'true';
        const giftId = Number(activeGiftBox.dataset.variantId);
        const giftItem = cart.items.find(i => i.id === giftId);

        if (giftItem && isSyncQuantity) {
          const giftIds = [...new Set(giftBoxes.map(b => Number(b.dataset.variantId)).filter(id => id > 0))];
          const nonGiftQty = cart.items.filter(i => {

            if (giftIds.includes(i.id)) return false;

            if (i.properties) {
              if (i.properties._novelty === 'true') return false;
              if (i.properties._product_addon === 'true') return false;
            }

            return true;
          }).reduce((sum, i) => sum + i.quantity, 0);

          if (giftItem.quantity !== nonGiftQty && nonGiftQty > 0) {
            updatesNeeded[giftItem.key] = nonGiftQty;
          } else if (nonGiftQty === 0 && giftItem.quantity > 0) {
            updatesNeeded[giftItem.key] = 0;
          }
        }
      }

      let requiredAddons = {};
      cart.items.forEach(item => {
        if (item.properties) {
          Object.keys(item.properties).forEach(key => {
            if (key.startsWith('_addon_once_')) {
              const addonId = Number(key.replace('_addon_once_', ''));
              if (addonId > 0 && item.properties[key] === 'true') {
                requiredAddons[addonId] = 1;
              }
            } else if (key.startsWith('_addon_')) {
              const addonId = Number(key.replace('_addon_', ''));
              if (addonId > 0 && item.properties[key] === 'true') {
                requiredAddons[addonId] = (requiredAddons[addonId] || 0) + item.quantity;
              }
            }
          });
        }
      });

      Object.keys(requiredAddons).forEach(addonIdStr => {
        const addonId = Number(addonIdStr);
        const requiredQty = requiredAddons[addonId];
        const addonItemInCart = cart.items.find(i => i.id === addonId);

        if (addonItemInCart) {
          if (addonItemInCart.quantity !== requiredQty) {
            updatesNeeded[addonItemInCart.key] = requiredQty;
          }
        } else {
          if (!itemsToAdd.find(i => i.id === addonId)) {
            itemsToAdd.push({ id: addonId, quantity: requiredQty, properties: { _product_addon: 'true' } });
          }
        }
      });

      cart.items.forEach(item => {
        if (item.properties && item.properties._product_addon === 'true') {
          if (!requiredAddons[item.id]) {
            updatesNeeded[item.key] = 0;
          }
        }
      });

      if (itemsToAdd.length > 0) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: itemsToAdd })
        });
        return this.triggerCartUpdate();
      }

      if (Object.keys(updatesNeeded).length > 0) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updatesNeeded })
        });
        this.triggerCartUpdate();
      }

    } catch (err) {
      console.error("[Universal Addon] Rules Check Error", err);
    } finally {
      this._isUpdatingRules = false;
    }
  }

  triggerCartUpdate() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
    }
  }
}

window.addEventListener('DOMContentLoaded', () => {
  window.universalAddonManager = new UniversalAddonManager();
});